import React, { useState } from 'react';
import axios from 'axios';
import { Shield, CheckCircle2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { API_BASE_URL } from '../../config';
import { cn } from '../../lib/utils';

interface GDPRConsentModalProps {
  isOpen: boolean;
  onAccept: () => void;
}

export default function GDPRConsentModal({ isOpen, onAccept }: GDPRConsentModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const handleAccept = async () => {
    setIsSubmitting(true);
    const token = localStorage.getItem('token');
    
    try {
      if (token) {
        // If user is logged in, save consent to backend
        await axios.post(
          `${API_BASE_URL}/user/gdpr-consent`,
          { accepted: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // If not logged in, save to localStorage
        localStorage.setItem('gdpr-consent', JSON.stringify({ accepted: true, timestamp: new Date().toISOString() }));
      }
      
      onAccept();
    } catch (error) {
      console.error('[GDPR] Error saving consent:', error);
      // Even if backend fails, save locally and proceed
      localStorage.setItem('gdpr-consent', JSON.stringify({ accepted: true, timestamp: new Date().toISOString() }));
      onAccept();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || isHidden) return null;

  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border text-foreground shadow-2xl transition-all duration-300",
      isExpanded ? "max-h-[80vh]" : "max-h-20"
    )}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="flex-shrink-0 mt-1">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <p className="text-sm font-bold text-foreground">
                  We use cookies to enhance your experience. By continuing, you agree to our use of cookies.
                </p>
                {!isExpanded && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsExpanded(true)}
                    className="h-7 px-3 text-xs text-muted-foreground hover:text-foreground border-border hover:bg-card"
                  >
                    View more
                  </Button>
                )}
              </div>

              {isExpanded && (
                <div className="space-y-3 text-xs text-foreground leading-relaxed mb-3 animate-in slide-in-from-top-2 duration-200">
                  <p>
                    In accordance with the <strong className="text-foreground">General Data Protection Regulation (GDPR)</strong>, 
                    we need your consent to process your personal data.
                  </p>
                  
                  <div className="bg-muted/50 rounded-lg p-3 border border-border space-y-2">
                    <p className="font-bold text-foreground text-[10px] uppercase tracking-wider mb-2">We use cookies for:</p>
                    <ul className="space-y-1.5 text-[11px]">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                        <span><strong>Authentication:</strong> To keep you logged in and secure your account</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                        <span><strong>Preferences:</strong> To remember your settings and preferences</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                        <span><strong>Analytics:</strong> To understand how you use our service and improve it</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                        <span><strong>Social Media Integration:</strong> To connect and publish to your social accounts</span>
                      </li>
                    </ul>
                  </div>

                  <p className="text-[11px] text-muted-foreground">
                    By clicking "Accept", you consent to our use of cookies and processing of your personal data 
                    as described in our <a href="/privacy" target="_blank" className="text-primary hover:text-primary/80 underline">Privacy Policy</a>.
                  </p>
                </div>
              )}

              {isExpanded && (
                <div className="flex items-center gap-2">
                  <a 
                    href="/privacy" 
                    target="_blank"
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Learn more
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <Button
              onClick={handleAccept}
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90 text-white font-bold h-12 px-8 text-sm"
            >
              {isSubmitting ? 'Processing...' : 'Accept'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsHidden(true)}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
